import SelectInput from 'assets/components/inputs/select/SelectInput';
import { getLocales } from 'assets/locales/Locale';
import { AutomationEffectMetadata, AutomationRuleLike, AutomationRuleSchema } from '../helpers/AutomationTypes';
import {
  getEffectByPropertyPath,
  getPathIdEffectValues,
  isEffectPropertyPathMissing,
  isEffectValueMissing,
  isPathIdEffectValueMissing,
  updateEffect,
  updatePathIdEffects,
} from '../helpers/ruleHelpers';

type Props = {
  rule: AutomationRuleLike;
  effect: AutomationEffectMetadata;
  effectIndex?: number;
  schema?: AutomationRuleSchema;
  readonly?: boolean;
  onChange(rule: AutomationRuleLike): void;
};

function getEffectPropertyKey(effect: AutomationEffectMetadata) {
  return effect.type === 'assign' ? effect.propertyPath : effect.propertyPathPrefix;
}

function getEffectPropertyOptions(schema: AutomationRuleSchema | undefined, effect: AutomationEffectMetadata) {
  const sourceEffects = schema?.effects?.length ? schema.effects : [effect];
  return sourceEffects.map((item) => ({
    id: getEffectPropertyKey(item),
    title: item.label,
  }));
}

export default function AutomationEffectEditor(props: Props) {
  const { lang } = getLocales();
  const availableEffects = props.schema?.effects?.length ? props.schema.effects : [props.effect];
  const effectPropertyOptions = getEffectPropertyOptions(props.schema, props.effect);
  const storedEffect = typeof props.effectIndex === 'number' ? props.rule.effects?.[props.effectIndex] : undefined;
  const storedEffectMetadata =
    storedEffect &&
    availableEffects.find(
      (effect) => effect.type === 'assign' && getEffectPropertyKey(effect) === storedEffect.propertyPath
    );
  const selectedEffect = storedEffectMetadata || props.effect;
  const selectedEffectPropertyKey = getEffectPropertyKey(selectedEffect);

  if (selectedEffect.type === 'pathIdMultiSelect') {
    const requiredError = isPathIdEffectValueMissing(props.rule, selectedEffect) ? lang.required : undefined;
    const propertyPathError = isEffectPropertyPathMissing(selectedEffect) ? lang.required : undefined;

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <SelectInput
          name="propertyPath"
          data={effectPropertyOptions}
          value={selectedEffectPropertyKey}
          error={propertyPathError}
          readOnly={true}
          disabled={true}
          onChange={({ propertyPath }) => {
            const nextEffect = availableEffects.find(
              (effect) => effect.type === 'assign' && getEffectPropertyKey(effect) === propertyPath
            );
            if (!nextEffect || nextEffect.type !== 'assign') return;

            props.onChange(
              updateEffect(props.rule, props.effectIndex, {
                propertyPath: nextEffect.propertyPath,
                value: nextEffect.options?.[0]?.id?.toString() ?? '',
              })
            );
          }}
        />
        &nbsp;
        <SelectInput
          name="selectedEffectIds"
          isMultiselect
          data={selectedEffect.options || []}
          value={getPathIdEffectValues(props.rule, selectedEffect)}
          error={requiredError}
          readOnly={props.readonly}
          disabled={props.readonly}
          onChange={({ selectedEffectIds }) =>
            props.onChange(updatePathIdEffects(props.rule, selectedEffect, selectedEffectIds || []))
          }
        />
      </div>
    );
  }

  const currentEffect = storedEffect || getEffectByPropertyPath(props.rule, selectedEffect.propertyPath);
  const requiredError = isEffectValueMissing(currentEffect) ? lang.required : undefined;
  const propertyPathError = isEffectPropertyPathMissing(selectedEffect, currentEffect) ? lang.required : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <SelectInput
        name="propertyPath"
        data={effectPropertyOptions}
        value={selectedEffectPropertyKey}
        error={propertyPathError}
        readOnly={true}
        disabled={true}
        onChange={({ propertyPath }) => {
          const nextEffect = availableEffects.find(
            (effect) => effect.type === 'assign' && getEffectPropertyKey(effect) === propertyPath
          );
          if (!nextEffect || nextEffect.type !== 'assign') return;

          props.onChange(
            updateEffect(props.rule, props.effectIndex, {
              propertyPath: nextEffect.propertyPath,
              value: nextEffect.options?.[0]?.id?.toString() ?? '',
            })
          );
        }}
      />
      &nbsp;
      <SelectInput
        name="effectValue"
        data={selectedEffect.options || []}
        value={currentEffect?.value ?? ''}
        error={requiredError}
        disabled={props.readonly}
        readOnly={props.readonly}
        onChange={({ effectValue }) =>
          props.onChange(
            updateEffect(props.rule, props.effectIndex, {
              propertyPath: selectedEffect.propertyPath,
              value: effectValue,
            })
          )
        }
      />
    </div>
  );
}
